import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from 'react-router-dom';
import { Calendar, CheckSquare, StickyNote, Paperclip, Download, ChevronDown, ChevronRight, GripVertical, Maximize2, Minimize2 } from 'lucide-react';
import { apiDownloadUrl } from '../../lib/api';
import type { JSONContent } from '@tiptap/react';
import { MeetingGroupCard, type MeetingGroup } from './MeetingGroupCard';
import { AnnotationCard } from './AnnotationCard';
import type { GridCardConfig, GroupName } from './layoutUtils';

interface ViewContentItem {
  source_type: 'meeting_instance' | 'task' | 'note' | 'attachment';
  source_id: string;
  title: string;
  description?: string | null;
  tag_name: string;
  tag_id: string;
  tag_color: string;
  meeting_date?: string;
  series_id?: string;
  series_name?: string;
  tagged_text?: string;
  block_id?: string | null;
  note_tag_id?: string;
  status?: string;
  priority?: string;
  content?: string | null;
  created_at?: string;
  updated_at?: string;
  filename?: string;
  mime_type?: string;
  size_bytes?: number;
}

interface Annotation {
  id: string;
  after_item_id: string | null;
  content: JSONContent;
}

/** Map card types to entity type values used by SHOW toggles */
const CARD_TYPE_TO_ENTITY: Record<string, string> = {
  meeting_group: 'meeting_instance',
  task: 'task',
  note: 'note',
  file: 'attachment',
};

/** Map card types to group names */
const CARD_TYPE_TO_GROUP: Record<string, GroupName> = {
  meeting_group: 'meetings',
  task: 'tasks',
  note: 'notes',
  file: 'files',
};

const GROUP_META: Record<GroupName, { label: string; icon: typeof Calendar }> = {
  meetings: { label: 'Meetings', icon: Calendar },
  tasks: { label: 'Tasks', icon: CheckSquare },
  notes: { label: 'Notes', icon: StickyNote },
  files: { label: 'Files', icon: Paperclip },
};

interface ViewGridLayoutProps {
  cards: GridCardConfig[];
  meetingGroups: MeetingGroup[];
  taskItems: ViewContentItem[];
  noteItems: ViewContentItem[];
  fileItems: ViewContentItem[];
  annotations: Annotation[];
  visibleTypes: Set<string>;
  groupOrder: GroupName[];
  groupWidths: Partial<Record<GroupName, 1 | 2>>;
  collapsedGroups: Set<string>;
  onGroupReorder: (newOrder: GroupName[]) => void;
  onToggleGroupWidth: (group: GroupName) => void;
  onToggleCollapse: (group: string) => void;
  onUpdateAnnotation: (id: string, content: JSONContent) => void;
  onDeleteAnnotation: (id: string) => void;
  onFilePreview: (att: { id: string; filename: string; mime_type: string }) => void;
  linkState?: { from: string; fromLabel: string };
}

const statusColors: Record<string, string> = {
  todo: '#64748b',
  in_progress: '#f59e0b',
  done: '#22c55e',
  blocked: '#ef4444',
};

export function ViewGridLayout({
  cards,
  meetingGroups,
  taskItems,
  noteItems,
  fileItems,
  annotations,
  visibleTypes,
  groupOrder,
  groupWidths,
  collapsedGroups,
  onGroupReorder,
  onToggleGroupWidth,
  onToggleCollapse,
  onUpdateAnnotation,
  onDeleteAnnotation,
  linkState,
  onFilePreview,
}: ViewGridLayoutProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Build lookup maps FIRST (before filtering)
  const meetingGroupMap = new Map(meetingGroups.map(g => [g.meeting_instance_id, g]));
  const annotationMap = new Map(annotations.map(a => [a.id, a]));
  const taskMap = new Map<string, ViewContentItem>();
  for (const t of taskItems) {
    if (!taskMap.has(t.source_id)) taskMap.set(t.source_id, t);
  }
  const noteMap = new Map<string, ViewContentItem>();
  for (const n of noteItems) {
    if (!noteMap.has(n.source_id)) noteMap.set(n.source_id, n);
  }
  const fileMap = new Map<string, ViewContentItem>();
  for (const f of fileItems) {
    if (!fileMap.has(f.source_id)) fileMap.set(f.source_id, f);
  }

  // Filter cards by type visibility AND data existence (Bug #1 fix)
  const filteredCards = cards.filter(card => {
    if (card.type === 'annotation') return true;
    const entityType = CARD_TYPE_TO_ENTITY[card.type];
    if (entityType && !visibleTypes.has(entityType)) return false;
    // Check data existence to prevent "not found" errors
    switch (card.type) {
      case 'meeting_group': return meetingGroupMap.has(card.ref_id);
      case 'task': return taskMap.has(card.ref_id);
      case 'note': return noteMap.has(card.ref_id);
      case 'file': return fileMap.has(card.ref_id);
      default: return true;
    }
  });

  // Group filtered cards by type
  const annotationCards = filteredCards.filter(c => c.type === 'annotation');
  const groupedCards = new Map<GroupName, GridCardConfig[]>();
  for (const card of filteredCards) {
    if (card.type === 'annotation') continue;
    const group = CARD_TYPE_TO_GROUP[card.type];
    if (!group) continue;
    if (!groupedCards.has(group)) groupedCards.set(group, []);
    groupedCards.get(group)!.push(card);
  }

  // Build ordered groups (only those with cards)
  const allGroups: GroupName[] = ['meetings', 'tasks', 'notes', 'files'];
  const orderedGroups = [
    ...groupOrder.filter(g => groupedCards.has(g)),
    ...allGroups.filter(g => !groupOrder.includes(g) && groupedCards.has(g)),
  ];

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedGroups.findIndex(g => g === active.id);
    const newIndex = orderedGroups.findIndex(g => g === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(orderedGroups, oldIndex, newIndex);
    onGroupReorder(reordered);
  }

  function renderCardContent(card: GridCardConfig) {
    switch (card.type) {
      case 'meeting_group': {
        const group = meetingGroupMap.get(card.ref_id);
        if (!group) return null;
        return <MeetingGroupCard group={group} />;
      }
      case 'task': {
        const task = taskMap.get(card.ref_id);
        if (!task) return null;
        return <TaskCardContent item={task} linkState={linkState} />;
      }
      case 'note': {
        const note = noteMap.get(card.ref_id);
        if (!note) return null;
        return <NoteCardContent item={note} linkState={linkState} />;
      }
      case 'file': {
        const file = fileMap.get(card.ref_id);
        if (!file) return null;
        return <FileCardContent item={file} onPreview={onFilePreview} />;
      }
      case 'annotation': {
        const ann = annotationMap.get(card.ref_id);
        if (!ann) return null;
        return (
          <AnnotationCard
            annotation={ann}
            onUpdate={onUpdateAnnotation}
            onDelete={onDeleteAnnotation}
          />
        );
      }
      default:
        return null;
    }
  }

  return (
    <div className="space-y-4">
      {/* Top-level annotations (full-width, outside groups) */}
      {annotationCards.length > 0 && (
        <div className="grid grid-cols-1 gap-4">
          {annotationCards.map(card => (
            <div key={card.id} className="bg-bg-card border border-border rounded-lg p-4">
              {renderCardContent(card)}
            </div>
          ))}
        </div>
      )}

      {/* Sortable grouped sections in a 2-column grid — groups are half or full width */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedGroups} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {orderedGroups.map(groupName => {
              const groupCards = groupedCards.get(groupName) ?? [];
              if (groupCards.length === 0) return null;
              const width = groupWidths[groupName] ?? 2;

              return (
                <SortableGridGroup
                  key={groupName}
                  groupName={groupName}
                  groupCards={groupCards}
                  width={width}
                  isCollapsed={collapsedGroups.has(groupName)}
                  onToggleCollapse={() => onToggleCollapse(groupName)}
                  onToggleWidth={() => onToggleGroupWidth(groupName)}
                  renderCardContent={renderCardContent}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableGridGroup({
  groupName,
  groupCards,
  width,
  isCollapsed,
  onToggleCollapse,
  onToggleWidth,
  renderCardContent,
}: {
  groupName: GroupName;
  groupCards: GridCardConfig[];
  width: 1 | 2;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onToggleWidth: () => void;
  renderCardContent: (card: GridCardConfig) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: groupName });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const meta = GROUP_META[groupName];
  const Icon = meta.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/section ${width === 2 ? 'md:col-span-2' : ''}`}
    >
      {/* Section header */}
      <div className="flex items-center gap-2 pt-1 pb-2">
        <button
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing p-0.5 text-text-muted hover:text-text-secondary transition-colors opacity-0 group-hover/section:opacity-100 flex-shrink-0"
          title="Drag to reorder"
        >
          <GripVertical size={12} />
        </button>
        <button
          onClick={onToggleCollapse}
          className="text-text-muted hover:text-text-secondary transition-colors"
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </button>
        <Icon size={14} className="text-text-muted" />
        <span className="text-xs font-medium text-text-secondary uppercase tracking-wider">
          {meta.label}
        </span>
        <span className="text-xs text-text-muted">({groupCards.length})</span>
        <button
          onClick={onToggleWidth}
          className="p-0.5 text-text-muted hover:text-text-primary transition-colors opacity-0 group-hover/section:opacity-100 hidden md:block ml-auto"
          title={width === 2 ? 'Half width' : 'Full width'}
        >
          {width === 2 ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
        </button>
      </div>

      {/* Cards stacked within group */}
      {!isCollapsed && (
        <div className="space-y-2">
          {groupCards.map(card => (
            <div key={card.id} className="bg-bg-card border border-border rounded-lg p-4">
              {renderCardContent(card)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskCardContent({ item, linkState }: { item: ViewContentItem; linkState?: { from: string; fromLabel: string } }) {
  const statusColor = statusColors[item.status ?? ''] ?? '#64748b';
  return (
    <Link
      to={`/tasks/${item.source_id}`}
      state={linkState}
      className="block hover:opacity-80 transition-opacity"
    >
      <div className="flex items-center gap-2 mb-1">
        <CheckSquare size={14} className="text-text-muted flex-shrink-0" />
        <span
          className="text-[10px] px-1.5 py-0.5 rounded uppercase font-medium"
          style={{ backgroundColor: statusColor + '20', color: statusColor }}
        >
          {(item.status ?? 'todo').replace('_', ' ')}
        </span>
      </div>
      <div className="flex items-start justify-between gap-2 mt-1">
        <span className="text-sm font-medium text-text-primary">{item.title}</span>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
        >
          {item.tag_name}
        </span>
      </div>
      {item.description && (
        <p className="text-xs text-text-muted line-clamp-2 mt-1">{extractPlainText(item.description)}</p>
      )}
    </Link>
  );
}

function NoteCardContent({ item, linkState }: { item: ViewContentItem; linkState?: { from: string; fromLabel: string } }) {
  return (
    <Link
      to={`/notes/${item.source_id}`}
      state={linkState}
      className="block hover:opacity-80 transition-opacity"
    >
      <div className="flex items-center gap-2 mb-1">
        <StickyNote size={14} className="text-text-muted flex-shrink-0" />
        <span className="text-xs text-text-muted uppercase tracking-wider">Note</span>
      </div>
      <div className="flex items-start justify-between gap-2 mt-1">
        <span className="text-sm font-medium text-text-primary">{item.title}</span>
        <span
          className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
        >
          {item.tag_name}
        </span>
      </div>
      {item.updated_at && (
        <p className="text-xs text-text-muted mt-1">
          Updated {new Date(item.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      )}
    </Link>
  );
}

/** Extract plain text from a TipTap JSON string or return as-is if plain text */
function extractPlainText(content: string | null | undefined): string {
  if (!content) return '';
  if (!content.startsWith('{')) return content;
  try {
    const doc = JSON.parse(content);
    return extractTextFromNode(doc).trim();
  } catch {
    return content;
  }
}
function extractTextFromNode(node: any): string {
  if (!node) return '';
  let text = '';
  if (node.text) text += node.text;
  if (node.content && Array.isArray(node.content)) {
    for (const child of node.content) {
      text += extractTextFromNode(child) + ' ';
    }
  }
  return text;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const GRID_DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function isGridPreviewable(mimeType: string): boolean {
  return mimeType.startsWith('image/') || mimeType === 'application/pdf' || mimeType === 'message/rfc822' || mimeType === GRID_DOCX_MIME;
}

function FileCardContent({ item, onPreview }: { item: ViewContentItem; onPreview: (att: { id: string; filename: string; mime_type: string }) => void }) {
  const mime = item.mime_type ?? '';
  const previewable = isGridPreviewable(mime);

  async function handleClick() {
    if (previewable) {
      onPreview({ id: item.source_id, filename: item.filename ?? item.title, mime_type: mime });
    } else {
      await handleDownload();
    }
  }

  async function handleDownload() {
    try {
      const blobUrl = await apiDownloadUrl(`/attachments/${item.source_id}/download`);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = item.filename ?? item.title;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }

  return (
    <button
      onClick={handleClick}
      className="block w-full text-left hover:opacity-80 transition-opacity cursor-pointer"
    >
      <div className="flex items-center gap-2 mb-1">
        <Paperclip size={14} className="text-text-muted flex-shrink-0" />
        <span className="text-xs text-text-muted uppercase tracking-wider">File</span>
        {item.size_bytes != null && (
          <span className="text-xs text-text-muted">{formatFileSize(item.size_bytes)}</span>
        )}
        <span
          role="button"
          tabIndex={0}
          className="ml-auto p-1 -m-1 rounded hover:bg-bg-hover transition-colors"
          title="Download"
          onClick={(e) => { e.stopPropagation(); handleDownload(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleDownload(); } }}
        >
          <Download size={12} className="text-text-muted" />
        </span>
      </div>
      <div className="flex items-start justify-between gap-2 mt-1">
        <span className="text-sm font-medium text-text-primary">{item.filename ?? item.title}</span>
        {item.tag_name && (
          <span
            className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: item.tag_color + '20', color: item.tag_color }}
          >
            {item.tag_name}
          </span>
        )}
      </div>
      {item.mime_type && (
        <p className="text-xs text-text-muted mt-1">{item.mime_type}</p>
      )}
    </button>
  );
}
